<template>
  <div class="search-input-holder" :class="disabled ? 'disabled' : ''">
    <label v-if="label">{{ label }}</label>
    <div class="input-wrapper">
      <input
        type="text"
        :value="searchValue"
        :data-qa="dataQa"
        :class="[
          'search-input',
          classes,
          `${isTyping ? 'typing' : ''}`,
          errorMessage?.class,
          variant,
        ]"
        :placeholder="placeholder"
        :name="name"
        :disabled="disabled"
        ref="searchInput"
        autocomplete="off"
        tabindex="0"
        @keydown.enter="handleSearch"
        @focus="inputFocus($event)"
        @blur="inputBlur($event)"
        @input="$emit('update:inputValue', handleInputChange($event))"
      />
      <icon-search
        v-if="!isTyping || variant === 'search-trigger'"
        color="dusty-gray"
        class="search-icon"
        data-qa="search"
        @click="handleSearch"
      />
      <icon-close
        v-if="isTyping && variant !== 'search-trigger'"
        color="dusty-gray"
        class="clear-icon"
        data-qa="clear"
        @click="handleClear"
      />
    </div>
    <span v-if="errorMessage?.message" class="error-message">
      {{ errorMessage.message }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import IconSearch from '../../assets/icons/IconSearch.vue';
import IconClose from '../../assets/icons/IconClose.vue';

interface FormSearchFieldProps {
  name: string;
  label?: string;
  classes?: string;
  placeholder?: string;
  inputValue: string;
  isInvalid?: boolean;
  errorMessageText?: string;
  required?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'search-trigger';
  dataQa?: string;
}

const props = withDefaults(defineProps<FormSearchFieldProps>(), {
  label: '',
  classes: '',
  placeholder: '',
  errorMessageText: '',
  required: false,
  disabled: false,
  variant: 'default',
  dataQa: '',
});

interface FormSearchFieldEmits {
  (e: 'update:inputValue', value: string): void;

  (e: 'search', value: string): void;

  (e: 'focus', value: Event): void;

  (e: 'blur', value: Event): void;
}

const emit = defineEmits<FormSearchFieldEmits>();

const isTyping = ref<boolean>(false);
const searchInput = ref<HTMLInputElement>(null);
const searchValue = ref(props.inputValue);

const handleInputChange = (event: Event) => {
  searchValue.value = (event.target as HTMLInputElement).value;
  return (event.target as HTMLInputElement).value;
};

const isDirty = ref(false);
watch(
  () => props.inputValue,
  async () => {
    isDirty.value = true;
  }
);

const errorMessage = computed(() => {
  if (props.isInvalid && isDirty.value) {
    return {
      class: 'error',
      message: props.errorMessageText,
    };
  }

  return {
    class: '',
    message: '',
  };
});

watch(searchValue, function () {
  isTyping.value = !!searchValue.value;
});

const inputFocus = (event: Event) => {
  emit('focus', event);
};
const inputBlur = (event: Event) => {
  emit('blur', event);
};

const handleSearch = () => {
  emit('search', searchValue.value);
};

const handleClear = () => {
  isTyping.value = !isTyping.value;
  emit('update:inputValue', '');
  searchValue.value = '';
  focus();
};

const focus = () => {
  searchInput.value.focus();
};

defineExpose({
  focus,
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

.search-input-holder {
  &.disabled {
    pointer-events: none;
  }

  &.disabled.default {
    background: map.get(colors.$colors, 'white');
    border: 1px solid map.get(colors.$colors, 'alto');
    border-radius: 4px;
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
  }

  &.disabled.default:active {
    border: 1px solid map.get(colors.$colors, 'alto');
  }

  &.disabled.default:hover {
    background: map.get(colors.$colors, 'white');
  }

  label {
    color: map.get(colors.$colors, 'alto');
  }

  .error-message {
    color: map.get(colors.$colors, 'flamingo');
    flex: none;
    flex-grow: 0;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    order: 1;
  }
}

.input-wrapper {
  max-width: 100%;
  position: relative;

  .search-input {
    background: map.get(colors.$colors, 'white');
    border: 1px solid transparent;
    border-bottom-color: map.get(colors.$colors, 'silver');
    border-radius: 4px 4px 0 0;
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
    color: map.get(colors.$colors, 'shuttle-gray');
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    padding: 10px 42px 10px 12px;
    width: 100%;
  }

  .search-input:hover {
    background: map.get(colors.$colors, 'selago');
  }

  .search-input:focus {
    border-color: map.get(colors.$colors, 'jewel');
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.25);
    outline: none;

    + :deep(.input-icon) {
      color: map.get(colors.$colors, 'shuttle-gray');
    }
  }

  .default input::-webkit-search-cancel-button {
    padding: 4px;
  }

  .default label {
    color: map.get(colors.$colors, 'fun-blue');
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20;
  }

  .clear-icon,
  .search-icon {
    cursor: pointer;
    position: absolute;
    right: 15px;
    top: 8px;
  }

  .search-trigger:active {
    color: map.get(colors.$colors, 'shuttle-gray');
  }

  .search-trigger::placeholder {
    color: map.get(colors.$colors, 'shuttle-gray');
  }
}
</style>
